import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Plus, Trash2, Edit, Save, X, Upload, Loader2, FileSpreadsheet, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_MS = 86400000;

// Shows at a glance whether a sponsorship is live, running out, or already off
// the site — the public pages hide anything past its end date.
const ExpiryBadge = ({ date }) => {
  const ends = new Date(date);
  const days = Math.ceil((ends - Date.now()) / DAY_MS);
  const label = ends.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const [tone, text] =
    days < 0 ? ['bg-red-100 text-red-700', `Expired ${label} — hidden from the site`]
    : days <= 30 ? ['bg-amber-100 text-amber-800', `Ends ${label} — ${days} day${days === 1 ? '' : 's'} left`]
    : ['bg-slate-100 text-slate-600', `Ends ${label}`];
  return (
    <span className={`inline-block mt-1.5 text-[11px] font-semibold px-2 py-0.5 rounded ${tone}`}
          data-testid="entity-expiry-badge">
      {text}
    </span>
  );
};

const EXPIRY_FIELD = {
  key: 'expires_at',
  label: 'Sponsorship ends',
  type: 'date',
  hint: 'Leave blank for no end date. Once the date passes they drop off the site automatically.',
};

const EntityManager = ({ entityType, title, fields, csvImport }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const fileInputRefs = useRef({});
  const csvRef = useRef(null);
  const { toast } = useToast();

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const handleImageUpload = async (fieldKey, file) => {
    if (!file) return;
    setUploading(fieldKey);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/upload`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: fd,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setFormData(prev => ({ ...prev, [fieldKey]: data.url }));
      toast({ title: "Uploaded", description: "Image uploaded successfully" });
    } catch (err) {
      toast({ title: "Error", description: "Image upload failed", variant: "destructive" });
    } finally {
      setUploading(null);
    }
  };

  const loadItems = useCallback(async () => {
    try {
      // include_expired so lapsed sponsors stay visible (and renewable) here,
      // even though the public site has already dropped them.
      const res = await fetch(`${BACKEND_URL}/api/${entityType}?include_expired=true`, { headers: getAuthHeader() });
      const data = await res.json();
      setItems(data);
    } catch (error) {
      console.error(`Error loading ${entityType}:`, error);
    } finally {
      setLoading(false);
    }
  }, [entityType]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleSave = async () => {
    try {
      const url = editing
        ? `${BACKEND_URL}/api/${entityType}/${editing}`
        : `${BACKEND_URL}/api/${entityType}`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Failed to save');
      toast({ title: "Saved", description: `${title.slice(0, -1)} saved successfully` });
      setEditing(null);
      setAdding(false);
      setFormData({});
      loadItems();
    } catch (error) {
      toast({ title: "Error", description: "Failed to save", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/${entityType}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      toast({ title: "Deleted", description: "Item deleted successfully" });
      loadItems();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setAdding(false);
    const data = {};
    // date inputs need YYYY-MM-DD, but the API returns a full timestamp
    fields.forEach(f => {
      const value = item[f.key] || '';
      data[f.key] = f.type === 'date' ? String(value).slice(0, 10) : value;
    });
    setFormData(data);
  };

  const startAdd = () => {
    setAdding(true);
    setEditing(null);
    const data = {};
    fields.forEach(f => { data[f.key] = ''; });
    setFormData(data);
  };

  const cancel = () => {
    setEditing(null);
    setAdding(false);
    setFormData({});
  };

  const handleCsvUpload = async (file) => {
    if (!file || !csvImport) return;
    setCsvBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}${csvImport.endpoint}`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      const created = data.created || 0;
      const skipped = data.skipped || 0;
      const errs = (data.errors || []).length;
      toast({
        title: `${created} ${title.toLowerCase()} imported`,
        description: `${skipped} skipped (duplicates), ${errs} errors.`,
      });
      if (errs) console.warn('CSV import errors:', data.errors);
      loadItems();
    } catch (e) {
      toast({ title: 'CSV import failed', description: e.message || 'Network error', variant: 'destructive' });
    } finally {
      setCsvBusy(false);
      if (csvRef.current) csvRef.current.value = '';
    }
  };

  const downloadCsvTemplate = () => {
    if (!csvImport?.template) return;
    const blob = new Blob([csvImport.template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entityType}-template.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="ghost" size="sm"><ArrowLeft size={18} className="mr-1" /> Back</Button>
            </Link>
            <h1 className="text-xl font-bold text-slate-900">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {csvImport && (
              <>
                <input
                  ref={csvRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => handleCsvUpload(e.target.files?.[0])}
                  data-testid={`csv-import-input-${entityType}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={downloadCsvTemplate}
                  data-testid={`csv-template-${entityType}`}
                  title="Download a CSV template"
                >
                  <Download size={15} className="mr-1" /> Template
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={csvBusy}
                  onClick={() => csvRef.current?.click()}
                  data-testid={`csv-import-btn-${entityType}`}
                >
                  {csvBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <FileSpreadsheet size={15} className="mr-1" />}
                  {csvBusy ? 'Importing…' : 'Import CSV'}
                </Button>
              </>
            )}
            <Button onClick={startAdd} className="bg-cyan-500 hover:bg-cyan-600">
              <Plus size={18} className="mr-1" /> Add New
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {(adding || editing) && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6" data-testid="entity-form">
            <h3 className="text-lg font-semibold mb-4">{editing ? 'Edit' : 'Add New'}</h3>
            <div className="grid gap-4">
              {fields.map(f => (
                <div key={f.key}>
                  <Label>{f.label}</Label>
                  {f.type === 'image' ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={formData[f.key] || ''}
                          onChange={e => setFormData(prev => ({ ...prev, [f.key]: e.target.value }))}
                          placeholder={f.placeholder || 'Paste URL or upload an image'}
                          data-testid={`input-${f.key}`}
                          className="flex-1"
                        />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          ref={el => { fileInputRefs.current[f.key] = el; }}
                          onChange={e => handleImageUpload(f.key, e.target.files[0])}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRefs.current[f.key]?.click()}
                          disabled={uploading === f.key}
                          data-testid={`upload-${f.key}`}
                          className="shrink-0"
                        >
                          {uploading === f.key ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                          <span className="ml-1.5">{uploading === f.key ? 'Uploading...' : 'Upload'}</span>
                        </Button>
                      </div>
                      {formData[f.key] && (
                        <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                          <img src={formData[f.key].startsWith('/') ? `${BACKEND_URL}${formData[f.key]}` : formData[f.key]} alt="Preview" className="w-full h-full object-cover" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <Input
                      type={f.type === 'date' ? 'date' : 'text'}
                      value={formData[f.key] || ''}
                      onChange={e => setFormData(prev => ({ ...prev, [f.key]: e.target.value }))}
                      placeholder={f.placeholder || f.label}
                      data-testid={`input-${f.key}`}
                    />
                  )}
                  {f.hint && <p className="text-xs text-gray-500 mt-1">{f.hint}</p>}
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSave} className="bg-cyan-500 hover:bg-cyan-600" data-testid="save-entity-btn">
                <Save size={16} className="mr-1" /> Save
              </Button>
              <Button variant="outline" onClick={cancel}><X size={16} className="mr-1" /> Cancel</Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
            <p className="text-gray-500 mb-4">No {title.toLowerCase()} yet</p>
            <Button onClick={startAdd} className="bg-cyan-500 hover:bg-cyan-600">
              <Plus size={18} className="mr-1" /> Add First
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(item => (
              <div key={item.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 flex items-center justify-between" data-testid={`entity-row-${item.id}`}>
                <div className="flex items-center gap-4">
                  {(item.logo || item.image) && (
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 shrink-0 border border-gray-200">
                      <img src={(item.logo || item.image).startsWith('/') ? `${BACKEND_URL}${item.logo || item.image}` : (item.logo || item.image)} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-slate-900">{item.name || item.title || 'Unnamed'}</p>
                    {item.link && <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-sm text-cyan-600 hover:underline">{item.link}</a>}
                    {item.description && <p className="text-sm text-gray-500 mt-1">{item.description}</p>}
                    {item.expires_at && <ExpiryBadge date={item.expires_at} />}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(item)} data-testid={`edit-${item.id}`}>
                    <Edit size={16} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-${item.id}`}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const BusinessesManager = () => (
  <EntityManager
    entityType="businesses"
    title="Local Businesses"
    fields={[
      { key: 'name', label: 'Business Name', placeholder: 'e.g. Paisley Bakery' },
      { key: 'description', label: 'Description', placeholder: 'Brief description' },
      { key: 'image', label: 'Image', placeholder: 'https://...', type: 'image' },
      { key: 'link', label: 'Website Link', placeholder: 'https://...' },
      { key: 'category', label: 'Category', placeholder: 'e.g. Food, Retail, Services' },
    ]}
    csvImport={{
      endpoint: '/api/admin/businesses/import-csv',
      template:
        'name,description,image,link,category,order\n' +
        'Paisley Bakery,Family-run bakery on the High Street,,https://example.com/bakery,Food,1\n' +
        'Renfrewshire Plumbing Co,Local plumbers serving Paisley & Renfrew,,https://example.com/plumbing,Services,2\n',
    }}
  />
);

export const AdvertisersManager = () => (
  <EntityManager
    entityType="advertisers"
    title="Advertisers"
    fields={[
      { key: 'name', label: 'Advertiser Name', placeholder: 'e.g. Local Store' },
      { key: 'logo', label: 'Logo / Image', placeholder: 'https://...', type: 'image' },
      { key: 'link', label: 'Website Link', placeholder: 'https://...' },
      EXPIRY_FIELD,
    ]}
  />
);

export const SupportersManager = () => (
  <EntityManager
    entityType="supporters"
    title="Supporters"
    fields={[
      { key: 'name', label: 'Supporter Name', placeholder: 'e.g. Renfrewshire Council' },
      { key: 'logo', label: 'Logo / Image', placeholder: 'https://...', type: 'image' },
      { key: 'link', label: 'Website Link', placeholder: 'https://...' },
      EXPIRY_FIELD,
    ]}
  />
);

export const FeaturedPartnersManager = () => (
  <EntityManager
    entityType="featured-partners"
    title="Featured Partners"
    fields={[
      { key: 'name', label: 'Partner Name', placeholder: 'e.g. Paisley First' },
      { key: 'logo', label: 'Logo / Image', placeholder: 'https://...', type: 'image' },
      { key: 'link', label: 'Website Link', placeholder: 'https://...' },
      { key: 'testimonial', label: 'Testimonial Quote', placeholder: 'What they say about partnering with Paisley...' },
      { key: 'partner_type', label: 'Type (business / advertiser / supporter)', placeholder: 'business' },
      { key: 'tier', label: 'Tier (none / bronze / silver / gold / platinum)', placeholder: 'none' },
      EXPIRY_FIELD,
    ]}
  />
);

// "Worth a closer look" — the 3 editorial cards on the homepage
// (FeaturedSections.jsx falls back to hardcoded defaults if this collection
// is empty; once you add 3 entries here, those defaults are replaced.)
export const FeaturedSectionsManager = () => (
  <EntityManager
    entityType="featured"
    title='"Worth a Closer Look" Cards'
    fields={[
      { key: 'title', label: 'Card Title', placeholder: 'e.g. Paisley Abbey' },
      { key: 'description', label: 'Description', placeholder: 'A short blurb shown on the card.' },
      { key: 'image', label: 'Card Image', placeholder: 'https://... or /api/files/...', type: 'image' },
      { key: 'link', label: 'Link (internal /history or external https://...)', placeholder: '/history' },
      { key: 'badge', label: 'Badge Label', placeholder: 'e.g. Heritage' },
      { key: 'order', label: 'Display Order (0,1,2)', placeholder: '0' },
    ]}
  />
);
